import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { IoSearch, IoReturnDownBack } from "react-icons/io5";
import "./commandPalette.css";

const CommandPalette = ({ config, localizedText }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const history = useHistory();

  const allCommands = [
    { label: "Dashboard", path: "/dashboard", section: "Navigate", icon: "⌂", shortcut: "G H" },
    { label: "Cards", path: "/dashboard/company-cards", section: "Navigate", icon: "◇", shortcut: "G K" },
    { label: "Assets & Balances", path: "/dashboard/portfolio/assets", section: "Treasury", icon: "◈", shortcut: "G A" },
    { label: "Transaction History", path: "/dashboard/portfolio/history", section: "Treasury", icon: "▤", shortcut: "G T" },
    { label: "Convert / FX", path: "/dashboard/conversion", section: "Treasury", icon: "⇄", perm: "can_convert", shortcut: "G C" },
    { label: "Send Payment", path: "/dashboard/payment/options", section: "Move Money", icon: "↗", perm: "can_pay", shortcut: "G P" },
    { label: "Pay Vendor", path: "/dashboard/payment/vendors", section: "Move Money", icon: "↗", perm: "can_pay_vendor" },
    { label: "Deposit Funds", path: "/dashboard/deposit", section: "Move Money", icon: "↓", perm: "can_deposit", shortcut: "G D" },
    { label: "Withdraw Funds", path: "/dashboard/withdraw", section: "Move Money", icon: "↑", perm: "can_withdraw", shortcut: "G W" },
    { label: "Monthly Report", path: "/dashboard/monthy-payment-report", section: "Reports", icon: "▦", perm: "can_mreport_payment" },
    { label: "Settings", path: "/dashboard/settings", section: "Tools", icon: "⚙", shortcut: "G S" },
  ];

  const commands = allCommands.filter((cmd) => {
    if (!cmd.perm) return true;
    if (!config?.permissions) return false;
    return config.permissions.some((p) => p.name === cmd.perm && p.value);
  });

  const filtered = query
    ? commands.filter((cmd) =>
        cmd.label.toLowerCase().includes(query.toLowerCase()),
      )
    : commands;

  const groupedCommands = filtered.reduce((acc, cmd) => {
    if (!acc[cmd.section]) acc[cmd.section] = [];
    acc[cmd.section].push(cmd);
    return acc;
  }, {});

  const flatFiltered = filtered;

  const open = useCallback(() => {
    setIsOpen(true);
    setQuery("");
    setSelectedIndex(0);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);

  const execute = useCallback(
    (cmd) => {
      close();
      history.push(cmd.path);
    },
    [close, history],
  );

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (isOpen) close();
        else open();
      }
      if (e.key === "Escape" && isOpen) {
        close();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, open, close]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, flatFiltered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && flatFiltered[selectedIndex]) {
      execute(flatFiltered[selectedIndex]);
    }
  };

  if (!isOpen) return null;

  let flatIndex = -1;

  return (
    <div className="command-palette-overlay" onClick={close}>
      <div
        className="command-palette"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="command-palette-input-row">
          <IoSearch className="command-palette-search-icon" />
          <input
            ref={inputRef}
            className="command-palette-input"
            placeholder="Search commands..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
          />
          <kbd className="command-palette-kbd">ESC</kbd>
        </div>
        <div className="command-palette-results">
          {Object.entries(groupedCommands).map(([section, cmds]) => (
            <div key={section} className="command-palette-section">
              <div className="command-palette-section-label">{section}</div>
              {cmds.map((cmd) => {
                flatIndex++;
                const idx = flatIndex;
                return (
                  <div
                    key={cmd.path}
                    className={
                      "command-palette-item" +
                      (idx === selectedIndex ? " selected" : "")
                    }
                    onClick={() => execute(cmd)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <span className="command-palette-item-icon">{cmd.icon}</span>
                    <span className="command-palette-item-label">{cmd.label}</span>
                    {cmd.shortcut && (
                      <span className="command-palette-item-shortcut">
                        {cmd.shortcut.split(" ").map((k, ki) => (
                          <kbd key={ki}>{k}</kbd>
                        ))}
                      </span>
                    )}
                    {idx === selectedIndex && (
                      <IoReturnDownBack className="command-palette-item-enter" />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {flatFiltered.length === 0 && (
            <div className="command-palette-empty">No results</div>
          )}
        </div>
        <div className="command-palette-footer">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>↵</kbd> select</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
